import React, {
  ComponentProps,
  MouseEventHandler,
  useCallback,
  useContext,
} from 'react'

import useSetImmersiveMode from '../../../hooks/useImmersiveModeHandlers'
import { ItemCardVariant } from '../ItemCard'

import {
  tripSavedItemBookNowClicked,
  tripSavedItemOfferDetailsClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import { SEARCH_FLIGHTS_COPY, VIEW_OFFER_COPY } from 'tripPlanner/config'
import TripContext from 'tripPlanner/contexts/TripContext'
import useRestoreCartButtonProps, {
  BOOK_NOW_BUTTON_TEXT,
} from 'tripPlanner/hooks/useRestoreCartButtonProps'
import { LETripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

interface Props {
  item: LETripItem
  className?: string
  variant: ItemCardVariant
}

function RestoreCartButtons({ item, className, variant }: Props) {
  const tripContext = useContext(TripContext)
  const curatedTripId = tripContext?.viewAsTemplate
    ? tripContext.tripId
    : undefined
  const canEdit = tripContext?.permissions.canEdit ?? false

  const { onClick, restoreCartUrl, label, productLink } =
    useRestoreCartButtonProps(item, variant, curatedTripId)
  const { setImmersiveMode } = useSetImmersiveMode()

  const source =
    variant === 'drawer' ? 'trip_drawer_all_saved_items' : 'trip_summary'
  const onClickViewButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    fireInteractionEvent(tripSavedItemOfferDetailsClicked(source))
    if (canEdit) {
      setImmersiveMode({
        tripId: item.tripId,
        sectionId: itemSectionId(item.id),
      })
    }
  }, [canEdit, item.id, item.tripId, setImmersiveMode, source])

  const onClickRestoreCartButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      if (e.currentTarget.textContent === BOOK_NOW_BUTTON_TEXT) {
        fireInteractionEvent(tripSavedItemBookNowClicked(source))
      }
      onClick(e)
    },
    [source, onClick],
  )
  const hasRestoreCartButton = canEdit && !!restoreCartUrl && !!label
  const viewCopy =
    item.type === 'FLIGHT' ? SEARCH_FLIGHTS_COPY : VIEW_OFFER_COPY

  const viewOfferButtonKind: ComponentProps<typeof TextButton>['kind'] =
    item.type === 'FLIGHT' && variant === 'drawer'
      ? 'primary'
      : canEdit && hasRestoreCartButton
      ? 'tertiary'
      : 'secondary'
  const restoreCartButtonKind: ComponentProps<typeof TextButton>['kind'] =
    variant === 'drawer' ? 'primary' : 'secondary'

  return (
    <Group
      direction="horizontal"
      className={className}
      verticalAlign="center"
      horizontalAlign="stretch"
      tabletHorizontalAlign="space-between"
      gap={12}
    >
      <TextButton
        kind={viewOfferButtonKind}
        to={productLink}
        onClick={onClickViewButton}
      >
        {viewCopy}
      </TextButton>
      {hasRestoreCartButton && (
        <TextButton
          kind={restoreCartButtonKind}
          to={restoreCartUrl}
          onClick={onClickRestoreCartButton}
        >
          {label}
        </TextButton>
      )}
    </Group>
  )
}

export default React.memo(RestoreCartButtons)
